import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import type { DropdownMenuItem } from 'libs/shared/ui';
import {
  AddIcon,
  DropdownMenu,
  IconButton,
  IconSizes,
  PersonAddIcon,
  RocketIcon,
  SchoolIcon
} from 'libs/shared/ui';

import { LoginState, useSelectLoginState } from 'Auth/data';
import { Breadcrumbs } from 'Breadcrumb';
import { SearchBar } from 'Common/SearchBar/SearchBar';
import { Logo } from 'Components';
import { useSelectAreTagsLoading, useSelectAvailableTagsWithTranslations } from 'Courses/data';
import { urls } from 'Router';
import { useAppDispatch } from 'Store';
import { saveIsStartBannerHidden } from 'User/data';

import { LanguageBar } from '../LanguageBar';
import { ThemeSwitcher } from '../ThemeSwitcher';
import { UserProfileModal } from '../UserProfileModal/UserProfileModal';

import {
  NavbarCenter,
  NavbarDivider,
  NavbarLeft,
  NavbarRight,
  StyledWrapper
} from './Navbar.styles';

type Props = {
  isForLogged: boolean;
  onCreateCourse?: () => void;
};

export const Navbar = ({ isForLogged, onCreateCourse }: Readonly<Props>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  const shouldShowSearchBar = isForLogged && location.pathname === '/';
  const loginState = useSelectLoginState();

  const availableTagsWithTranslations = useSelectAvailableTagsWithTranslations();
  const areTagsLoading = useSelectAreTagsLoading();

  const handleAddMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null);
  };

  const handleCreateCourse = () => {
    if (onCreateCourse) {
      onCreateCourse();
    } else {
      navigate(urls.createCoursePage);
    }
    handleAddMenuClose();
  };

  const handleShowStartBanner = () => {
    dispatch(
      saveIsStartBannerHidden({
        isStartBannerHidden: false,
        isLogged: loginState === LoginState.LOGGED
      })
    );
    handleAddMenuClose();
  };

  const menuItems: DropdownMenuItem[] = [
    {
      id: 'create-course',
      icon: <SchoolIcon fontSize={IconSizes.small} />,
      label: t('navbar.createCourse'),
      onClick: handleCreateCourse
    },
    {
      id: 'invite-friend',
      icon: <PersonAddIcon fontSize={IconSizes.small} />,
      label: t('navbar.inviteFriend'),
      onClick: () => {}
    },
    {
      id: 'show-start-banner',
      icon: <RocketIcon fontSize={IconSizes.small} />,
      label: t('navbar.whatIsTreneo'),
      onClick: handleShowStartBanner
    }
  ];

  const handleLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate({ pathname: urls.homePage, search: '' }, { replace: true });
  };

  return (
    <StyledWrapper>
      <NavbarDivider>
        <NavbarLeft>
          <Link to={urls.homePage} onClick={handleLogoClick}>
            <Logo variant="menu" />
          </Link>
          {isForLogged && <Breadcrumbs />}
        </NavbarLeft>

        {shouldShowSearchBar && (
          <NavbarCenter>
            <SearchBar
              availableTags={availableTagsWithTranslations}
              areTagsLoading={areTagsLoading}
              placeholder={t('coursesPage.searchCourse')}
            />

            <IconButton
              onClick={handleAddMenuOpen}
              variant="primary"
              size="medium"
              aria-label={t('navbar.addMenu')}
            >
              <AddIcon />
            </IconButton>

            <DropdownMenu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={handleAddMenuClose}
              items={menuItems}
            />
          </NavbarCenter>
        )}

        <NavbarRight>
          {isForLogged && <UserProfileModal />}

          <LanguageBar />
          <ThemeSwitcher />
        </NavbarRight>
      </NavbarDivider>
    </StyledWrapper>
  );
};
